/**
 * Move one item to an explicit place in an already-ordered id list and return
 * the whole list. Both administrative reorder endpoints take the complete
 * order rather than a delta, so a move control submits every id, not the pair
 * it changed.
 *
 * This is a move, not a swap: everything between the old and new place shifts
 * by one, which is what "put this third" means to the person typing it. For an
 * adjacent move the two are the same thing.
 *
 * A target off either end clamps, and an id the list does not hold returns the
 * order unchanged - the control offering such a move is the thing to fix, and
 * a no-op request is cheaper than a rejected one.
 */
export function movedToIndex(orderedIds: readonly string[], id: string, targetIndex: number): string[] {
	const ordered = [...orderedIds]
	const index = ordered.indexOf(id)
	if (index === -1) return ordered
	const target = Math.max(0, Math.min(targetIndex, ordered.length - 1))
	if (target === index) return ordered
	ordered.splice(index, 1)
	ordered.splice(target, 0, id)
	return ordered
}

/** One place earlier or later in the order, expressed as {@link movedToIndex} so both controls share one algorithm. */
export function movedOnePlace(orderedIds: readonly string[], id: string, direction: 'up' | 'down'): string[] {
	const index = orderedIds.indexOf(id)
	if (index === -1) return [...orderedIds]
	return movedToIndex(orderedIds, id, direction === 'up' ? index - 1 : index + 1)
}
